/**
 * Welle 9 (pre85b): Virtueller Hintergrund für den Berater.
 *
 * - Personensegmentierung via MediaPipe Selfie Segmentation, same-origin gebündelt
 *   unter /mediapipe/ (kein Runtime-CDN, offline-tauglich).
 * - Das Script wird per <script>-Tag geladen (nicht über den Bundler), damit der
 *   große WASM-Loader nicht in den App-Bundle wandert.
 * - FAIL-SAFE: Solange das Modell nicht bereit ist (oder bei jedem Fehler) liefert
 *   process() null → die Portrait-Pipeline zeichnet das normale Bild. Der Call-Pfad
 *   kann dadurch nicht brechen.
 */

export type BgMode = 'none' | 'blur' | 'image';
export interface BgConfig {
  mode: BgMode;
  /** Blur-Stärke in px (nur mode==='blur'). */
  blur?: number;
  /** Bildquelle (Preset-URL oder Data-URL; nur mode==='image'). */
  imageSrc?: string;
}

export const BLUR_LEVELS = [
  { key: 'light', label: 'Leicht', px: 6 },
  { key: 'medium', label: 'Mittel', px: 12 },
  { key: 'strong', label: 'Stark', px: 22 },
];

export const PRESET_BACKGROUNDS = [
  { id: 'office-blue', label: 'Blau', src: '/backgrounds/office-blue.jpg' },
  { id: 'neutral-graphite', label: 'Graphit', src: '/backgrounds/neutral-graphite.jpg' },
  { id: 'calm-green', label: 'Grün', src: '/backgrounds/calm-green.jpg' },
  { id: 'warm-bokeh', label: 'Bokeh', src: '/backgrounds/warm-bokeh.jpg' },
];

const LS_KEY = 'bamoa_bg_config';
const LS_CUSTOM = 'bamoa_bg_custom';

export function getBgConfig(): BgConfig {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* */ }
  return { mode: 'none' };
}
export function setBgConfig(cfg: BgConfig): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(cfg));
    if (cfg.mode !== 'none') localStorage.setItem('bamoa_bg_last', JSON.stringify(cfg));
  } catch { /* */ }
  window.dispatchEvent(new CustomEvent('bamoa:bg-changed', { detail: cfg }));
}
export function getLastBg(): BgConfig {
  try {
    const raw = localStorage.getItem('bamoa_bg_last');
    if (raw) return JSON.parse(raw);
  } catch { /* */ }
  return { mode: 'blur', blur: 12 };
}
/** Schnell-Umschaltung für den InCall-Button: aktiv ↔ aus. */
export function toggleBg(): BgConfig {
  const cur = getBgConfig();
  const next: BgConfig = cur.mode !== 'none' ? { mode: 'none' } : getLastBg();
  setBgConfig(next);
  return next;
}
export function getCustomBg(): string | null {
  try { return localStorage.getItem(LS_CUSTOM); } catch { return null; }
}
export function setCustomBg(dataUrl: string | null): void {
  try {
    if (dataUrl) localStorage.setItem(LS_CUSTOM, dataUrl);
    else localStorage.removeItem(LS_CUSTOM);
  } catch { /* */ }
}

// pre85c-fix: horizontales Spiegeln (unabhängig vom Hintergrund)
export function getCamFlip(): boolean {
  try { return localStorage.getItem('bamoa_cam_flip') === '1'; } catch { return false; }
}
export function setCamFlip(on: boolean): void {
  try { localStorage.setItem('bamoa_cam_flip', on ? '1' : '0'); } catch { /* */ }
  window.dispatchEvent(new CustomEvent('bamoa:cam-flip', { detail: on }));
}

/** Eigenes Bild auf <=1280px herunterskalieren und als JPEG-Data-URL liefern (platzsparend). */
export function fileToScaledDataUrl(file: File, maxW = 1280): Promise<string> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxW / img.width);
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const c = document.createElement('canvas');
      c.width = w; c.height = h;
      const ctx = c.getContext('2d');
      if (!ctx) { URL.revokeObjectURL(url); reject(new Error('no ctx')); return; }
      ctx.drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(url);
      resolve(c.toDataURL('image/jpeg', 0.85));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('image load failed')); };
    img.src = url;
  });
}

// ---- MediaPipe-Lader (einmalig, per <script>) ----
let mpLoading: Promise<any> | null = null;
function loadSelfieSegmentation(): Promise<any> {
  if ((window as any).SelfieSegmentation) return Promise.resolve((window as any).SelfieSegmentation);
  if (mpLoading) return mpLoading;
  mpLoading = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = '/mediapipe/selfie_segmentation.js';
    s.async = true;
    s.onload = () => {
      const SS = (window as any).SelfieSegmentation;
      if (SS) resolve(SS); else reject(new Error('SelfieSegmentation nicht verfügbar'));
    };
    s.onerror = () => reject(new Error('MediaPipe-Script konnte nicht geladen werden'));
    document.head.appendChild(s);
  });
  return mpLoading;
}

/**
 * Kapselt ein SelfieSegmentation-Modell + Compositing. Läuft eine eigene async-Schleife
 * (send → onResults), entkoppelt von der rAF-Zeichenschleife der Pipeline.
 */
export class BackgroundCompositor {
  private seg: any = null;
  private running = false;
  private looping = false;
  private ready = false;
  private active = false;
  private video: HTMLVideoElement | null = null;
  private mask: HTMLCanvasElement;
  private mctx: CanvasRenderingContext2D;
  private hasMask = false;

  constructor() {
    this.mask = document.createElement('canvas');
    this.mctx = this.mask.getContext('2d')!;
  }

  setActive(on: boolean) { this.active = on; if (on) void this.ensure(); }

  attach(video: HTMLVideoElement) { this.video = video; if (this.active) void this.ensure(); }

  private async ensure() {
    if (this.seg || this.running) return;
    this.running = true;
    try {
      const SS = await loadSelfieSegmentation();
      this.seg = new SS({ locateFile: (file: string) => `/mediapipe/${file}` });
      this.seg.setOptions({ modelSelection: 1, selfieMode: false });
      this.seg.onResults((r: any) => this.onResults(r));
      this.ready = true;
      this.startLoop();
    } catch (e) {
      console.warn('[bg] MediaPipe konnte nicht initialisiert werden — Hintergrund deaktiviert', e);
      this.ready = false;
    }
  }

  private startLoop() {
    if (this.looping) return;
    this.looping = true;
    let last = 0;
    const tick = async (ts: number) => {
      if (!this.looping) return;
      const v = this.video;
      // Segmentierung auf ~26 fps drosseln — entlastet die CPU, Maske ändert sich langsam.
      if (this.ready && this.seg && v && v.videoWidth > 0 && this.active && ts - last > 38) {
        last = ts;
        try { await this.seg.send({ image: v }); } catch { /* */ }
      }
      if (this.looping) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  private onResults(results: any) {
    const v = this.video;
    if (!v) return;
    const w = v.videoWidth, h = v.videoHeight;
    if (w === 0 || h === 0) return;
    if (this.mask.width !== w || this.mask.height !== h) { this.mask.width = w; this.mask.height = h; }
    const ctx = this.mctx;
    ctx.clearRect(0, 0, w, h);
    // Maske weichzeichnen → Kanten gefeathered statt pixelig. Die Maske trägt die Alpha.
    ctx.filter = `blur(${Math.max(3, Math.round(w / 170))}px)`;
    ctx.drawImage(results.segmentationMask, 0, 0, w, h);
    ctx.filter = 'none';
    this.hasMask = true;
  }

  /** Aktuelle (geglättete) Personen-Maske, oder null wenn (noch) nicht bereit / inaktiv. */
  getMask(): HTMLCanvasElement | null {
    return this.active && this.ready && this.hasMask ? this.mask : null;
  }

  stop() {
    this.looping = false;
    this.running = false;
    this.active = false;
    try { this.seg?.close?.(); } catch { /* */ }
    this.seg = null;
    this.ready = false;
    this.hasMask = false;
    this.video = null;
  }
}
